import React, { useState, useEffect } from 'react';
import {
  GraduationCap, Briefcase, DollarSign, Calendar, BookOpen, Target, Star,
  Plus, Edit, Trash2, Eye, EyeOff, Search, Filter, RefreshCw, Save, X,
  ChevronDown, ChevronUp, ArrowLeft, Check, AlertCircle, Clock, MapPin
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  sideHustles, summerJobs, extracurriculars, interestCategories,
  examCalendar, examFees, studyMethods, examSubjectsEnhanced
} from '../lib/supabase';

// ==================== CONSTANTS ====================
const JOB_TYPES = [
  { value: 'formal_program', label: 'Formal Program' },
  { value: 'tourism', label: 'Tourism & Hospitality' },
  { value: 'retail', label: 'Retail & Food Service' },
  { value: 'bpo', label: 'BPO / Call Center' },
  { value: 'community', label: 'Community / Volunteer' },
  { value: 'agricultural', label: 'Agricultural' }
];

const ACTIVITY_CATEGORIES = [
  { value: 'academic', label: 'Academic Clubs' },
  { value: 'performing_arts', label: 'Performing Arts' },
  { value: 'sports', label: 'Sports & Athletics' },
  { value: 'service_leadership', label: 'Service & Leadership' },
  { value: 'creative_arts', label: 'Creative Arts' }
];

const ACTIVITY_TYPES = [
  { value: 'school_based', label: 'School-Based' },
  { value: 'external', label: 'External Program' }
];

const EXAM_TYPES = [
  { value: 'csec', label: 'CSEC' },
  { value: 'cape', label: 'CAPE' },
  { value: 'pep', label: 'PEP' },
  { value: 'nvqj', label: 'NVQ-J' }
];

const EVENT_TYPES = [
  { value: 'deadline', label: 'Deadline' },
  { value: 'exam', label: 'Examination' },
  { value: 'results', label: 'Results Release' }
];

// ==================== MAIN ADMIN PAGE ====================
export const AdminStudentEngagementPage = ({ setPage }) => {
  const [activeTab, setActiveTab] = useState('side_hustles');
  const { user } = useAuth();

  const tabs = [
    { id: 'side_hustles', name: 'Side Hustles', icon: DollarSign, count: null },
    { id: 'summer_jobs', name: 'Summer Jobs', icon: Briefcase, count: null },
    { id: 'extracurriculars', name: 'Extracurriculars', icon: Star, count: null },
    { id: 'interest_categories', name: 'Career Fields', icon: Target, count: null },
    { id: 'exam_calendar', name: 'Exam Calendar', icon: Calendar, count: null },
    { id: 'exam_subjects', name: 'Exam Subjects', icon: BookOpen, count: null },
    { id: 'study_methods', name: 'Study Methods', icon: GraduationCap, count: null }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setPage('admin')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Student Engagement Tools</h1>
                <p className="text-sm text-gray-500">Manage side hustles, jobs, activities, and exam resources</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b overflow-x-auto">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'border-brand-600 text-brand-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {activeTab === 'side_hustles' && <SideHustlesTab />}
        {activeTab === 'summer_jobs' && <SummerJobsTab />}
        {activeTab === 'extracurriculars' && <ExtracurricularsTab />}
        {activeTab === 'interest_categories' && <InterestCategoriesTab />}
        {activeTab === 'exam_calendar' && <ExamCalendarTab />}
        {activeTab === 'exam_subjects' && <ExamSubjectsTab />}
        {activeTab === 'study_methods' && <StudyMethodsTab />}
      </div>
    </div>
  );
};

// ==================== SIDE HUSTLES TAB ====================
const SideHustlesTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    age_appropriate: '14+',
    earning_potential_min: '',
    earning_potential_max: '',
    earning_frequency: 'per task',
    time_commitment: '',
    skills_needed: [],
    how_to_start: [],
    tips_for_success: [],
    student_story: '',
    parent_note: '',
    career_connections: [],
    display_order: 0,
    is_active: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const { data, error } = await sideHustles.getAllAdmin();
    if (!error && data) setItems(data);
    setLoading(false);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      age_appropriate: '14+',
      earning_potential_min: '',
      earning_potential_max: '',
      earning_frequency: 'per task',
      time_commitment: '',
      skills_needed: [],
      how_to_start: [],
      tips_for_success: [],
      student_story: '',
      parent_note: '',
      career_connections: [],
      display_order: 0,
      is_active: true
    });
    setEditingItem(null);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      ...item,
      skills_needed: item.skills_needed || [],
      how_to_start: item.how_to_start || [],
      tips_for_success: item.tips_for_success || [],
      career_connections: item.career_connections || []
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const dataToSave = {
      ...formData,
      earning_potential_min: parseInt(formData.earning_potential_min) || null,
      earning_potential_max: parseInt(formData.earning_potential_max) || null,
      display_order: parseInt(formData.display_order) || 0
    };

    let result;
    if (editingItem) {
      result = await sideHustles.update(editingItem.id, dataToSave);
    } else {
      result = await sideHustles.create(dataToSave);
    }

    if (!result.error) {
      setShowModal(false);
      resetForm();
      loadData();
    } else {
      alert('Error saving: ' + result.error.message);
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      await sideHustles.delete(id);
      loadData();
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    await sideHustles.toggleActive(id, !currentStatus);
    loadData();
  };

  const handleArrayInput = (field, value) => {
    const items = value.split('\n').filter(item => item.trim());
    setFormData({ ...formData, [field]: items });
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold">Side Hustles ({items.length})</h2>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 bg-brand-600 text-white px-4 py-2 rounded-lg hover:bg-brand-700"
        >
          <Plus className="w-4 h-4" /> Add Side Hustle
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Name</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Age</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Earnings</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Order</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {items.map(item => (
              <tr key={item.id} className={!item.is_active ? 'bg-gray-50 opacity-60' : ''}>
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-800">{item.name}</div>
                  <div className="text-sm text-gray-500 truncate max-w-xs">{item.description}</div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{item.age_appropriate}</td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  J${item.earning_potential_min?.toLocaleString()} - J${item.earning_potential_max?.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{item.display_order}</td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => handleToggleActive(item.id, item.is_active)}
                    className={`px-2 py-1 rounded text-xs font-medium ${
                      item.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {item.is_active ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => handleEdit(item)} className="p-1 text-blue-600 hover:bg-blue-50 rounded">
                    <Edit className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(item.id)} className="p-1 text-red-600 hover:bg-red-50 rounded ml-2">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold">{editingItem ? 'Edit' : 'Add'} Side Hustle</h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Age Appropriate</label>
                  <input
                    type="text"
                    value={formData.age_appropriate}
                    onChange={(e) => setFormData({ ...formData, age_appropriate: e.target.value })}
                    placeholder="e.g., 14+"
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Time Commitment</label>
                  <input
                    type="text"
                    value={formData.time_commitment}
                    onChange={(e) => setFormData({ ...formData, time_commitment: e.target.value })}
                    placeholder="e.g., 2-4 hours per week"
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Min Earnings (JMD)</label>
                  <input
                    type="number"
                    value={formData.earning_potential_min}
                    onChange={(e) => setFormData({ ...formData, earning_potential_min: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Earnings (JMD)</label>
                  <input
                    type="number"
                    value={formData.earning_potential_max}
                    onChange={(e) => setFormData({ ...formData, earning_potential_max: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Earning Frequency</label>
                  <input
                    type="text"
                    value={formData.earning_frequency}
                    onChange={(e) => setFormData({ ...formData, earning_frequency: e.target.value })}
                    placeholder="e.g., per hour, per task"
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                  <input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => setFormData({ ...formData, display_order: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Skills Needed (one per line)</label>
                  <textarea
                    value={formData.skills_needed.join('\n')}
                    onChange={(e) => handleArrayInput('skills_needed', e.target.value)}
                    rows={3}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">How to Start (one per line)</label>
                  <textarea
                    value={formData.how_to_start.join('\n')}
                    onChange={(e) => handleArrayInput('how_to_start', e.target.value)}
                    rows={4}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tips for Success (one per line)</label>
                  <textarea
                    value={formData.tips_for_success.join('\n')}
                    onChange={(e) => handleArrayInput('tips_for_success', e.target.value)}
                    rows={4}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Student Story</label>
                  <textarea
                    value={formData.student_story}
                    onChange={(e) => setFormData({ ...formData, student_story: e.target.value })}
                    rows={2}
                    placeholder="Example success story..."
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Parent Note</label>
                  <textarea
                    value={formData.parent_note}
                    onChange={(e) => setFormData({ ...formData, parent_note: e.target.value })}
                    rows={2}
                    placeholder="Note for parents..."
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Career Connections (one per line)</label>
                  <textarea
                    value={formData.career_connections.join('\n')}
                    onChange={(e) => handleArrayInput('career_connections', e.target.value)}
                    rows={2}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div className="col-span-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">Active</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="border-t px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !formData.name}
                className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? 'Saving...' : <><Save className="w-4 h-4" /> Save</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== SUMMER JOBS TAB ====================
const SummerJobsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterType, setFilterType] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    job_type: 'formal_program',
    description: '',
    positions: [],
    age_requirement: '18+',
    locations: [],
    duration: '',
    pay: '',
    is_paid: true,
    eligibility: [],
    requirements: [],
    application_method: '',
    application_timeline: '',
    application_contact: '',
    what_you_gain: [],
    tips: [],
    display_order: 0,
    is_active: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const { data, error } = await summerJobs.getAllAdmin();
    if (!error && data) setItems(data);
    setLoading(false);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      organization: '',
      job_type: 'formal_program',
      description: '',
      positions: [],
      age_requirement: '18+',
      locations: [],
      duration: '',
      pay: '',
      is_paid: true,
      eligibility: [],
      requirements: [],
      application_method: '',
      application_timeline: '',
      application_contact: '',
      what_you_gain: [],
      tips: [],
      display_order: 0,
      is_active: true
    });
    setEditingItem(null);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      ...item,
      positions: item.positions || [],
      locations: item.locations || [],
      eligibility: item.eligibility || [],
      requirements: item.requirements || [],
      what_you_gain: item.what_you_gain || [],
      tips: item.tips || []
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const dataToSave = {
      ...formData,
      display_order: parseInt(formData.display_order) || 0
    };

    let result;
    if (editingItem) {
      result = await summerJobs.update(editingItem.id, dataToSave);
    } else {
      result = await summerJobs.create(dataToSave);
    }

    if (!result.error) {
      setShowModal(false);
      resetForm();
      loadData();
    } else {
      alert('Error saving: ' + result.error.message);
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      await summerJobs.delete(id);
      loadData();
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    await summerJobs.toggleActive(id, !currentStatus);
    loadData();
  };

  const handleArrayInput = (field, value) => {
    const items = value.split('\n').filter(item => item.trim());
    setFormData({ ...formData, [field]: items });
  };

  const filteredItems = filterType
    ? items.filter(item => item.job_type === filterType)
    : items;

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold">Summer Jobs ({filteredItems.length})</h2>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="border rounded-lg px-3 py-1 text-sm"
          >
            <option value="">All Types</option>
            {JOB_TYPES.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 bg-brand-600 text-white px-4 py-2 rounded-lg hover:bg-brand-700"
        >
          <Plus className="w-4 h-4" /> Add Job
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Name / Organization</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Type</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Locations</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Pay</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filteredItems.map(item => (
              <tr key={item.id} className={!item.is_active ? 'bg-gray-50 opacity-60' : ''}>
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-800">{item.name}</div>
                  <div className="text-sm text-gray-500">{item.organization}</div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {JOB_TYPES.find(t => t.value === item.job_type)?.label || item.job_type}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {item.locations?.slice(0, 2).join(', ')}{item.locations?.length > 2 ? '...' : ''}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {item.is_paid ? item.pay || 'Paid' : 'Unpaid'}
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => handleToggleActive(item.id, item.is_active)}
                    className={`px-2 py-1 rounded text-xs font-medium ${
                      item.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {item.is_active ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => handleEdit(item)} className="p-1 text-blue-600 hover:bg-blue-50 rounded">
                    <Edit className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(item.id)} className="p-1 text-red-600 hover:bg-red-50 rounded ml-2">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal - Similar structure to SideHustlesTab */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold">{editingItem ? 'Edit' : 'Add'} Summer Job</h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
                  <input
                    type="text"
                    value={formData.organization}
                    onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Job Type *</label>
                  <select
                    value={formData.job_type}
                    onChange={(e) => setFormData({ ...formData, job_type: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    {JOB_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Age Requirement</label>
                  <input
                    type="text"
                    value={formData.age_requirement}
                    onChange={(e) => setFormData({ ...formData, age_requirement: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
                  <input
                    type="text"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pay</label>
                  <input
                    type="text"
                    value={formData.pay}
                    onChange={(e) => setFormData({ ...formData, pay: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div className="flex items-center">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.is_paid}
                      onChange={(e) => setFormData({ ...formData, is_paid: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">Paid Position</span>
                  </label>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Positions (one per line)</label>
                  <textarea
                    value={formData.positions.join('\n')}
                    onChange={(e) => handleArrayInput('positions', e.target.value)}
                    rows={3}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Locations (one per line)</label>
                  <textarea
                    value={formData.locations.join('\n')}
                    onChange={(e) => handleArrayInput('locations', e.target.value)}
                    rows={2}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Eligibility (one per line)</label>
                  <textarea
                    value={formData.eligibility.join('\n')}
                    onChange={(e) => handleArrayInput('eligibility', e.target.value)}
                    rows={3}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Application Method</label>
                  <input
                    type="text"
                    value={formData.application_method}
                    onChange={(e) => setFormData({ ...formData, application_method: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Application Timeline</label>
                  <input
                    type="text"
                    value={formData.application_timeline}
                    onChange={(e) => setFormData({ ...formData, application_timeline: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Application Contact</label>
                  <input
                    type="text"
                    value={formData.application_contact}
                    onChange={(e) => setFormData({ ...formData, application_contact: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">What You Gain (one per line)</label>
                  <textarea
                    value={formData.what_you_gain.join('\n')}
                    onChange={(e) => handleArrayInput('what_you_gain', e.target.value)}
                    rows={3}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
              </div>
            </div>
            <div className="border-t px-6 py-4 flex justify-end gap-3">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !formData.name}
                className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? 'Saving...' : <><Save className="w-4 h-4" /> Save</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== PLACEHOLDER TABS (to be expanded) ====================
const ExtracurricularsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const { data } = await extracurriculars.getAllAdmin();
    if (data) setItems(data);
    setLoading(false);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold">Extracurricular Activities ({items.length})</h2>
        <button className="flex items-center gap-2 bg-brand-600 text-white px-4 py-2 rounded-lg hover:bg-brand-700">
          <Plus className="w-4 h-4" /> Add Activity
        </button>
      </div>
      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-500">
            {items.length} activities in database. Full editor coming soon.
          </p>
          <ul className="mt-4 space-y-2">
            {items.slice(0, 10).map(item => (
              <li key={item.id} className="text-sm text-gray-700">
                • {item.name} ({item.category} - {item.activity_type})
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const InterestCategoriesTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const { data } = await interestCategories.getAllAdmin();
    if (data) setItems(data);
    setLoading(false);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold">Career Interest Categories ({items.length})</h2>
        <button className="flex items-center gap-2 bg-brand-600 text-white px-4 py-2 rounded-lg hover:bg-brand-700">
          <Plus className="w-4 h-4" /> Add Category
        </button>
      </div>
      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map(item => (
            <div key={item.id} className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-2xl">{item.icon}</span>
                <span className="font-semibold">{item.name}</span>
              </div>
              <p className="text-sm text-gray-600">{item.description}</p>
              <div className="mt-2 text-xs text-gray-500">
                {item.related_careers?.length || 0} careers • {item.key_employers?.length || 0} employers
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ExamCalendarTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const { data } = await examCalendar.getAllAdmin();
    if (data) setItems(data);
    setLoading(false);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold">Exam Calendar Dates ({items.length})</h2>
        <button className="flex items-center gap-2 bg-brand-600 text-white px-4 py-2 rounded-lg hover:bg-brand-700">
          <Plus className="w-4 h-4" /> Add Date
        </button>
      </div>
      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Exam</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Event</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Date</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Type</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {items.map(item => (
                <tr key={item.id}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-800">{item.exam_type?.toUpperCase()}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{item.event_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{item.event_date}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs ${
                      item.event_type === 'deadline' ? 'bg-red-100 text-red-700' :
                      item.event_type === 'exam' ? 'bg-blue-100 text-blue-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {item.event_type}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const ExamSubjectsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const { data } = await examSubjectsEnhanced.getAllAdmin();
    if (data) setItems(data);
    setLoading(false);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold">Exam Subjects ({items.length})</h2>
        <button className="flex items-center gap-2 bg-brand-600 text-white px-4 py-2 rounded-lg hover:bg-brand-700">
          <Plus className="w-4 h-4" /> Add Subject
        </button>
      </div>
      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-500">{items.length} subjects in database.</p>
          <div className="mt-4 grid md:grid-cols-3 gap-2">
            {items.map(item => (
              <div key={item.id} className="text-sm p-2 bg-gray-50 rounded">
                <span className="font-medium">{item.name}</span>
                <span className="text-gray-500 ml-2">({item.exam_type?.toUpperCase()})</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const StudyMethodsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const { data } = await studyMethods.getAllAdmin();
    if (data) setItems(data);
    setLoading(false);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold">Study Methods ({items.length})</h2>
        <button className="flex items-center gap-2 bg-brand-600 text-white px-4 py-2 rounded-lg hover:bg-brand-700">
          <Plus className="w-4 h-4" /> Add Method
        </button>
      </div>
      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map(item => (
            <div key={item.id} className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold text-gray-800">{item.name}</h3>
              <p className="text-sm text-gray-600 mt-1">{item.description}</p>
              <p className="text-xs text-gray-500 mt-2">Best for: {item.best_for}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminStudentEngagementPage;
